import React, { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const Banner = ({ id, src, type, overlay, children, poster, eagerLoad = false }) => {
    const bannerRef = useRef(null);

    const [isInView, setIsInView] = useState(eagerLoad); 
    const [videoReady, setVideoReady] = useState(false);

    useEffect(() => {
        if (eagerLoad) {
            return; 
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );

        if (bannerRef.current) {
            observer.observe(bannerRef.current);
        }

        return () => {
            if (bannerRef.current) {
                observer.disconnect();
            }
        };
    }, [eagerLoad]);

    const bgStyle = useMemo(() => {
        if (type === 'imagen' && isInView) {
            return {
                backgroundImage: `url(${src})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            };
        }
        return {};
    }, [type, src, isInView]);

    return (
        <div
            id={id || undefined}
            ref={bannerRef}
            className={`banner-section${videoReady ? ' video-ready' : ''}`}
            style={bgStyle}
        >
            {type === 'video' && isInView && (
                <video
                    className="banner-video"
                    src={src}
                    poster={poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload={eagerLoad ? "auto" : "metadata"}
                    fetchPriority="high"
                    onLoadedData={() => setVideoReady(true)}
                />
            )}

            {overlay === 'gradient' && <div className="banner-gradient" />}
            {overlay === 'black' && <div className="overlay-container" />}
            
            {children}
        </div>
    );
};

Banner.propTypes = {
    id: PropTypes.string,
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['video', 'imagen']),
    overlay: PropTypes.oneOf(['gradient', 'black']),
    children: PropTypes.node,
    poster: PropTypes.string,
    eagerLoad: PropTypes.bool,
};

export default Banner;